import React, {
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  isValid,
  parse,
} from 'date-fns'

import { validate } from 'lib/validation'

import {
  ContainerProps,
  Inputs,
  OnBlurOnChange,
} from './types'

import DateInput from './index.presentational'

const isoDateString = (monthValue: number | null, dayValue: number | null, yearValue: number | null): string => (yearValue && monthValue && dayValue && `${yearValue}-${`0${monthValue}`.slice(-2)}-${`0${dayValue}`.slice(-2)}`) || ''

const isTouched = (inputs: Inputs, touchedProp: boolean, recentlyTouchedInput?: string): boolean => {
  return touchedProp || Object.keys(inputs).every(key => key === recentlyTouchedInput || inputs[key].touched || inputs[key].value)
}

const DateContainer: React.FC<ContainerProps> = ({
  name,
  onBlur: onBlurProp,
  onChange: onChangeProp,
  rules,
  touched: touchedProp,
  value,
  ...props
}): React.ReactElement => {
  const values = useMemo(() => {
    const values: {
      month: number | null,
      day: number | null,
      year: number | null,
    } = {
      month: null,
      day: null,
      year: null,
    }

    const date = parse(value as string)

    if (isValid(date)) {
      values.month = date.getMonth() + 1
      values.day = date.getDate()
      values.year = date.getFullYear()
    }

    return values
  }, [ value ])

  const [
    monthValue,
    setMonthValue,
  ] = useState(values.month || null)
  const [
    dayValue,
    setDayValue,
  ] = useState(values.day || null)
  const [
    yearValue,
    setYearValue,
  ] = useState(values.year || null)

  const [
    monthTouched,
    setMonthTouched,
  ] = useState((values.month && true) || false)
  const [
    dayTouched,
    setDayTouched,
  ] = useState((values.day && true) || false)
  const [
    yearTouched,
    setYearTouched,
  ] = useState((values.year && true) || false)

  const monthRef = useRef<HTMLInputElement>(null)
  const dayRef = useRef<HTMLInputElement>(null)
  const yearRef = useRef<HTMLInputElement>(null)

  const inputs: Inputs = useMemo(() => ({
    month: {
      ref: monthRef,
      setTouched: setMonthTouched,
      touched: monthTouched,
      value: monthValue,
    },
    day: {
      ref: dayRef,
      setTouched: setDayTouched,
      touched: dayTouched,
      value: dayValue,
    },
    year: {
      ref: yearRef,
      setTouched: setYearTouched,
      touched: yearTouched,
      value: yearValue,
    },
  }), [
    monthValue,
    dayValue,
    yearValue,
    monthTouched,
    dayTouched,
    yearTouched,
  ])

  const onBlur: OnBlurOnChange = (event): void => {
    let inputName: string|undefined = undefined
    if (name) {
      inputName = event.target.name.replace(new RegExp(`^${name}-`), '')

      inputs[inputName].setTouched(true)
    }

    if (isTouched(inputs, touchedProp || false, inputName)) {
      onBlurProp && onBlurProp({ target: { value } } as unknown as React.ChangeEvent<HTMLInputElement>)
    }
  }

  const onChange: OnBlurOnChange = (): void => {
    const month = inputs.month.ref.current?.value || ''
    const day = inputs.day.ref.current?.value || ''
    const year = inputs.year.ref.current?.value || ''

    setMonthValue(parseInt(month) || null)
    setDayValue(parseInt(day) || null)
    setYearValue(parseInt(year) || null)

    const value = isoDateString(parseInt(month), parseInt(day), parseInt(year))
    onChangeProp && onChangeProp({ target: { value } } as unknown as React.ChangeEvent<HTMLInputElement>)
  }

  const touched = useMemo(() => isTouched(inputs, touchedProp || false), [
    inputs,
    touchedProp,
  ])

  const valid = useMemo(() => {
    const value = isoDateString(monthValue, dayValue, yearValue)

    return !rules || validate(rules, value).isValid
  }, [
    monthValue,
    dayValue,
    yearValue,
    rules,
  ])

  return React.createElement(
    DateInput,
    {
      ...props,
      inputs,
      name,
      onBlur,
      onChange,
      touched,
      valid,
    },
    null,
  )
}

export * from './types'

export default DateContainer
